import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>!</div>
            <h2 style={styles.heading}>Something went wrong</h2>
            <p style={styles.message}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={styles.button}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 32,
    background: 'var(--bg-light)',
  },
  card: {
    textAlign: 'center',
    padding: 48,
    background: 'var(--bg-white)',
    borderRadius: 8,
    boxShadow: 'var(--shadow-md)',
    maxWidth: 440,
    width: '100%',
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#FDE7E9',
    color: '#A4262C',
    fontSize: '1.5rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  heading: {
    fontSize: '1.29rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 8,
  },
  message: {
    fontSize: '0.93rem',
    color: 'var(--text-secondary)',
    marginBottom: 24,
  },
  button: {
    padding: '6px 20px',
    background: 'var(--primary)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 4,
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
